import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import Litepicker from '@/components/Base/Litepicker';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import Chart from '@/components/Base/Chart';
import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

// ── Helpers ────────────────────────────────────────────────────────────────

function todayMYT() {
  return dayjs().format('YYYY-MM-DD');
}

function todayISORange() {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    startISO: dayjs(today).startOf('day').toISOString(),
    endISO:   dayjs(today).endOf('day').toISOString(),
  };
}

// ── Colour palette ─────────────────────────────────────────────────────────

const COLORS = [
  '#93c5fd','#86efac','#fcd34d','#fca5a5','#c4b5fd',
  '#f9a8d4','#6ee7b7','#fdba74','#a5f3fc','#d9f99d',
  '#fbcfe8','#bfdbfe','#bbf7d0','#fef08a','#fecaca',
];

function hexAlpha(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── Shared datalabels config ───────────────────────────────────────────────

const BAR_DATALABELS = {
  display: true,
  anchor: 'end' as const,
  align: 'end' as const,
  offset: 2,
  color: '#1e293b',
  font: { size: 9, weight: 600 as const },
  formatter: (value: number) => (value > 0 ? value : ''),
};

const STACKED_DATALABELS = {
  display: true,
  anchor: 'center' as const,
  align: 'center' as const,
  color: '#1e293b',
  font: { size: 8, weight: 600 as const },
  formatter: (value: number) => (value > 0 ? value : ''),
};

// ── Shared chart.js option helpers ─────────────────────────────────────────

const BASE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 500 },
  plugins: {
    legend: {
      labels: { boxWidth: 10, boxHeight: 10, font: { size: 10 }, padding: 8 },
    },
    tooltip: { bodyFont: { size: 11 }, titleFont: { size: 11 }, padding: 8, cornerRadius: 8 },
  },
  scales: {
    x: {
      ticks: { font: { size: 10 }, maxRotation: 0 },
      grid: { color: 'rgba(148,163,184,0.25)', borderDash: [2, 2] as [number, number] },
      border: { display: false },
    },
    y: {
      ticks: { font: { size: 10 } },
      grid: { color: 'rgba(148,163,184,0.25)', borderDash: [2, 2] as [number, number] },
      border: { display: false },
    },
  },
};

// ── Types ──────────────────────────────────────────────────────────────────

type NewVsExistingPoint = { date: string; newUsers: number; existingUsers: number };
type RedemptionByMonth  = { month: string; count: number };
type BrandShare         = { brand: string; count: number };
type AgePoint           = { age: string; count: number };

type DashboardStats = {
  uniqueContactCount:      number;
  tngUsed:                 number;
  tngTotal:                number;
  tngPct:                  number;
  newVsExisting:           NewVsExistingPoint[];
  redemptionByMonth:       RedemptionByMonth[];
  brandShare:              BrandShare[];
  redemptionByDistributor: Record<string, number | string>[];
  distributors:            string[];
  ageDistribution:         AgePoint[];
  redemptionByStateRace:   Record<string, number | string>[];
  races:                   string[];
};

const DASHBOARD_REFRESH_MS = 5 * 60 * 1000;

// ── Component ──────────────────────────────────────────────────────────────

export default function Home() {
  const todayStr = todayMYT();
  const [dateRange, setDateRange]           = useState(`${todayStr} - ${todayStr}`);
  const [confirmedRange, setConfirmedRange] = useState<{ startISO: string; endISO: string } | null>(todayISORange());
  const [stats,   setStats]   = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const dateRangeISO = useMemo(() => {
    const raw = (dateRange || '').trim();
    if (!raw) return null;
    const [startRaw, endRaw] = raw.split(' - ');
    const startStr = (startRaw || '').trim();
    const endStr   = ((endRaw || '') || startStr).trim();
    if (!startStr) return null;
    const start = dayjs(startStr);
    const end   = dayjs(endStr);
    if (!start.isValid() || !end.isValid()) return null;
    return { startISO: start.startOf('day').toISOString(), endISO: end.endOf('day').toISOString() };
  }, [dateRange]);

  const handleSearch = () => {
    if (!dateRangeISO) return;
    setConfirmedRange({ ...dateRangeISO });
  };

  const fetchDashboardStats = useCallback(
    async (
      range: { startISO: string; endISO: string },
      options?: { background?: boolean }
    ) => {
      const background = options?.background ?? false;
      if (background && abortRef.current) return;

      abortRef.current?.abort();
      if (!background) {
        setIsRefreshing(false);
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);
      if (background) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const origin = getApiOrigin();
      const params = new URLSearchParams({ startDate: range.startISO, endDate: range.endISO });

      try {
        const response = await fetch(`${origin}/api/v1/admin/dashboard?${params.toString()}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const json = (await response.json()) as { ok: boolean; data: DashboardStats };
        setStats(json.data);
        setLastUpdatedAt(new Date());
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        const isActiveRequest = abortRef.current === controller;
        if (isActiveRequest) {
          abortRef.current = null;
        }
        if (background && isActiveRequest) {
          setIsRefreshing(false);
        } else if (!background && isActiveRequest) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (!confirmedRange) return;
    void fetchDashboardStats(confirmedRange);
  }, [confirmedRange, fetchDashboardStats]);

  useEffect(() => {
    if (!confirmedRange) return;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void fetchDashboardStats(confirmedRange, { background: true });
    }, DASHBOARD_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [confirmedRange, fetchDashboardStats]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // ── Chart data memos ────────────────────────────────────────────────────

  const newVsExistingData = useMemo(() => {
    const d = stats?.newVsExisting ?? [];
    return {
      labels: d.map((p) => p.date.slice(5)),
      datasets: [
        {
          label: 'Existing Users',
          data: d.map((p) => p.existingUsers),
          borderColor: '#3b82f6',
          backgroundColor: hexAlpha('#3b82f6', 0.12),
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.35,
          fill: true,
        },
        {
          label: 'New Users',
          data: d.map((p) => p.newUsers),
          borderColor: '#ef4444',
          backgroundColor: hexAlpha('#ef4444', 0.12),
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.35,
          fill: true,
        },
      ],
    };
  }, [stats?.newVsExisting]);

  const newVsExistingOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { ...BASE_OPTIONS.plugins.legend, position: 'top' as const },
      datalabels: { display: false },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  const redemptionByMonthData = useMemo(() => {
    const d = stats?.redemptionByMonth ?? [];
    return {
      labels: d.map((p) => p.month),
      datasets: [{
        label: 'Redeemed',
        data: d.map((p) => p.count),
        backgroundColor: '#c4b5fd',
        borderColor: '#a78bfa',
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.redemptionByMonth]);

  const redemptionByMonthOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  const brandShareData = useMemo(() => {
    const d = stats?.brandShare ?? [];
    return {
      labels: d.map((p) => p.brand),
      datasets: [{
        data: d.map((p) => p.count),
        backgroundColor: d.map((_, i) => COLORS[i % COLORS.length]),
        borderColor: d.map((_, i) => COLORS[i % COLORS.length]),
        borderWidth: 1.5,
        hoverOffset: 6,
      }],
    };
  }, [stats?.brandShare]);

  const brandShareOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    cutout: '78%',
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        color: '#1e293b',
        font: { size: 10, weight: 700 as const },
        formatter: (value: number, ctx: any) => {
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
          return pct + '%';
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            const pct   = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0';
            return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
  }), []);

  const distData = useMemo(() => {
    const d   = stats?.redemptionByDistributor ?? [];
    const dists = stats?.distributors ?? [];
    return {
      labels: d.map((p) => p.date as string),
      datasets: dists.map((dist, idx) => ({
        label: dist,
        data: d.map((p) => (p[dist] as number) ?? 0),
        backgroundColor: COLORS[idx % COLORS.length],
        borderColor: COLORS[idx % COLORS.length],
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 20,
        maxBarThickness: 24,
        stack: 'a',
      })),
    };
  }, [stats?.redemptionByDistributor, stats?.distributors]);

  const distOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: {
        ...BASE_OPTIONS.plugins.legend,
        position: 'right' as const,
        labels: {
          ...BASE_OPTIONS.plugins.legend.labels,
          boxWidth: 8,
          boxHeight: 8,
          font: { size: 9 },
          padding: 6,
        },
      },
      datalabels: { ...STACKED_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: { ...BASE_OPTIONS.scales.x, stacked: true },
      y: { ...BASE_OPTIONS.scales.y, stacked: true, beginAtZero: true, grace: '15%' },
    },
  }), []);

  const ageData = useMemo(() => {
    const d = stats?.ageDistribution ?? [];
    return {
      labels: d.map((p) => p.age),
      datasets: [{
        label: 'Count',
        data: d.map((p) => p.count),
        backgroundColor: '#93c5fd',
        borderColor: '#60a5fa',
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.ageDistribution]);

  const ageOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  const stateRaceData = useMemo(() => {
    const d     = stats?.redemptionByStateRace ?? [];
    const races = stats?.races ?? [];
    return {
      labels: d.map((p) => p.state as string),
      datasets: races.map((race, idx) => ({
        label: race,
        data: d.map((p) => (p[race] as number) ?? 0),
        backgroundColor: COLORS[idx % COLORS.length],
        borderColor: COLORS[idx % COLORS.length],
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 16,
        maxBarThickness: 20,
        stack: 'a',
      })),
    };
  }, [stats?.redemptionByStateRace, stats?.races]);

  const stateRaceOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    indexAxis: 'y' as const,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: {
        ...BASE_OPTIONS.plugins.legend,
        position: 'top' as const,
        labels: {
          ...BASE_OPTIONS.plugins.legend.labels,
          boxWidth: 8,
          boxHeight: 8,
          font: { size: 9 },
          padding: 6,
        },
      },
      datalabels: { ...STACKED_DATALABELS, font: { size: 7, weight: 600 as const } },
    },
    scales: {
      x: {
        stacked: true,
        grace: '15%',
        ticks: { font: { size: 9 } },
        grid: { color: 'rgba(148,163,184,0.25)', borderDash: [2, 2] as [number, number] },
        border: { display: false },
      },
      y: {
        stacked: true,
        ticks: { font: { size: 9 }, padding: 4 },
        grid: { display: false },
        border: { display: false },
        offset: true,
      },
    },
  }), []);

  // ── OOS Experience ────────────────────────────────────────────────────
  const oosExperienceData = useMemo(() => {
    const d = stats?.oosExperience ?? [];
    return {
      labels: d.map((p: { label: string; count: number }) => p.label),
      datasets: [{
        label: 'Count',
        data: d.map((p: { label: string; count: number }) => p.count),
        backgroundColor: '#fcd34d',
        borderColor: '#fbbf24',
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.oosExperience]);

  const oosExperienceOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: {
        ...BASE_OPTIONS.scales.x,
        ticks: { ...BASE_OPTIONS.scales.x.ticks, maxRotation: 45, minRotation: 30 },
      },
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  // ── EVO Consumption Count ─────────────────────────────────────────────
  const evoConsumptionData = useMemo(() => {
    const d = stats?.evoConsumption ?? [];
    return {
      labels: d.map((p: { label: string; count: number }) => p.label),
      datasets: [{
        label: 'Count',
        data: d.map((p: { label: string; count: number }) => p.count),
        backgroundColor: '#86efac',
        borderColor: '#4ade80',
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.evoConsumption]);

  const evoConsumptionOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: {
        ...BASE_OPTIONS.scales.x,
        ticks: { ...BASE_OPTIONS.scales.x.ticks, maxRotation: 45, minRotation: 30 },
      },
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  // ── Key Account Redemption Count ──────────────────────────────────────
  const keyAccountData = useMemo(() => {
    const d = stats?.keyAccountRedemption ?? [];
    return {
      labels: d.map((p: { label: string; count: number }) => p.label),
      datasets: [{
        label: 'Count',
        data: d.map((p: { label: string; count: number }) => p.count),
        backgroundColor: '#93c5fd',
        borderColor: '#60a5fa',
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.keyAccountRedemption]);

  const keyAccountOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: {
        ...BASE_OPTIONS.scales.x,
        ticks: {
          ...BASE_OPTIONS.scales.x.ticks,
          maxRotation: 45,
          minRotation: 30,
        },
      },
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  // ── Render ──────────────────────────────────────────────────────────────

  const chartBox = 'box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-3 aspect-square shadow-sm flex flex-col';
  const chartTitle = 'text-sm font-semibold text-slate-600 dark:text-slate-300 text-center mb-2';
  const loadingEl  = <div className="flex-1 flex items-center justify-center text-slate-400 text-sm animate-pulse">Loading…</div>;
  const showLoadingState = loading && !stats;

  return (
    <div className="p-6">

      {/* ── Toolbar ── */}
      <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative inline-flex">
            <Lucide icon="Calendar" className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]" />
            <Litepicker
              value={dateRange}
              onChange={(e) => setDateRange((e.target?.value || '').trim())}
              options={{ autoApply: true, singleMode: false, numberOfColumns: 2, numberOfMonths: 2, showWeekNumbers: true, dropdowns: { minYear: 2024, maxYear: null, months: true, years: true }, format: 'YYYY-MM-DD' }}
              className="w-[270px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
            />
          </div>
          <Button variant="primary" onClick={handleSearch} disabled={!dateRangeISO || loading} className="w-full sm:w-auto">
            {loading
              ? <><Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />Loading…</>
              : <><Lucide icon="Search" className="stroke-[1.3] w-4 h-4 mr-2" />Search</>}
          </Button>
        </div>
        <div className="inline-flex items-center text-xs text-white/95">
          {isRefreshing ? (
            <>
              <Lucide icon="Loader" className="stroke-[1.3] w-3.5 h-3.5 mr-2 animate-spin" />
              Refreshing dashboard...
            </>
          ) : lastUpdatedAt ? (
            <>
              <Lucide icon="Clock3" className="stroke-[1.3] w-3.5 h-3.5 mr-2" />
              Last updated at {dayjs(lastUpdatedAt).format('HH:mm:ss')}
            </>
          ) : null}
        </div>
      </div>

      {/* ── Error ── */}
      {error && <div className="mb-6 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">{error}</div>}

      {/* ── Row 1: summary stats ── */}
      <div className="mb-5">
        <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm flex items-center gap-0 divide-x divide-slate-200 dark:divide-darkmode-400">
          <div className="flex flex-1 items-center justify-center gap-3 pr-6">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">Unique Contact Collected:</p>
            <p className="text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100 flex-shrink-0">
              {showLoadingState ? <span className="text-xl text-slate-400 animate-pulse">…</span> : (stats?.uniqueContactCount ?? 0).toLocaleString()}
            </p>
          </div>
          <div className="flex flex-1 items-center justify-center pl-6">
            <div className="inline-flex flex-col items-start gap-1">
              <div className="flex items-center gap-3">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">TnG Redemption Count:</p>
                {showLoadingState
                  ? <span className="text-xl text-slate-400 animate-pulse flex-shrink-0">…</span>
                  : (
                    <div className="flex items-baseline gap-2 flex-shrink-0 whitespace-nowrap">
                      <span className="text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100">{(stats?.tngUsed ?? 0).toLocaleString()}</span>
                      <span className="text-lg font-semibold text-slate-400 dark:text-slate-500">/ {(stats?.tngTotal ?? 0).toLocaleString()}</span>
                      <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">({stats?.tngPct ?? 0}% Used)</span>
                    </div>
                  )
                }
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Since 9th May 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">

        {/* [0] New Users vs Existing Users */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>New Users vs Existing Users</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="line" data={newVsExistingData} options={newVsExistingOptions} /></div>}
        </div>

        {/* [1] Redemption by Month */}
        <div className={chartBox}>
          <p className={chartTitle}>Redemption by Month</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={redemptionByMonthData} options={redemptionByMonthOptions} /></div>}
        </div>

        {/* [2] Smoking Brands Share */}
        <div className={chartBox}>
          <p className={chartTitle}>Smoking Brands Share</p>
          {showLoadingState
            ? loadingEl
            : (() => {
                const d     = stats?.brandShare ?? [];
                const total = d.reduce((s, x) => s + x.count, 0);
                return (
                  <div className="flex-1 flex items-center min-h-0">
                    <div className="w-[80%] h-full"><Chart type="doughnut" data={brandShareData} options={brandShareOptions} /></div>
                    <div className="w-[20%] flex flex-col justify-center gap-1.5 pl-1 overflow-y-auto max-h-full flex-shrink-0">
                      {d.map((item, idx) => (
                        <div key={item.brand} className="flex items-center gap-1 min-w-0">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS[idx % COLORS.length] }} />
                          <span className="text-[9px] text-slate-600 dark:text-slate-400 truncate leading-tight">{item.brand}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()
          }
        </div>

        {/* [3] Redemption by Distributor */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>Redemption by Distributor</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={distData} options={distOptions} /></div>}
        </div>

        {/* [4] Age Range */}
        <div className={chartBox}>
          <p className={chartTitle}>Age Range</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={ageData} options={ageOptions} /></div>}
        </div>

        {/* [5] Redemption by State & Race */}
        <div className={chartBox}>
          <p className={chartTitle}>Redemption by State &amp; Race</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={stateRaceData} options={stateRaceOptions} /></div>}
        </div>

        {/* [6] Key Account Redemption Count */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>Key Account Redemption Count</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={keyAccountData} options={keyAccountOptions} /></div>}
        </div>

        {/* [7] EVO Consumption Count */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>EVO Consumption Count</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={evoConsumptionData} options={evoConsumptionOptions} /></div>}
        </div>

        {/* [8] OOS Experience */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>OOS Experience</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={oosExperienceData} options={oosExperienceOptions} /></div>}
        </div>

      </div>
    </div>
  );
}
